import { Button, Radio, TextInput, Tooltip, Typography, Widget } from '@neo4j-ndl/react';
import type { IpFilter } from '@nx/state';
import { useActiveOrg } from '@nx/state';
import { useEffect, useState } from 'react';

import { AllowListTable } from './allow-list-table';
import { isValidIpRange } from './helpers';
import type { AllowListEntry } from './types';
import { MAX_ALLOW_LIST_SIZE } from './types';

interface AllowListSelectorProps {
  onChange: (allowList: AllowListEntry[]) => void;
  isReadOnly?: boolean;
  value?: IpFilter;
}

export const AllowListSelector = ({ onChange, isReadOnly = true, value }: AllowListSelectorProps) => {
  const [allSelected, setAllSelected] = useState<boolean>(true);
  const [allowList, setAllowList] = useState<AllowListEntry[]>([]);
  const [ipInput, setIpInput] = useState<string>('');
  const [descriptionInput, setDescriptionInput] = useState<string>('');
  const activeOrg = useActiveOrg();

  const maxAllowListSize = MAX_ALLOW_LIST_SIZE[activeOrg.planType];
  const exceedsMaxSize = allowList.length >= maxAllowListSize;

  useEffect(() => {
    if (!value) {
      setAllSelected(true);
    } else {
      setAllSelected(
        value.allowList.length === 0 || value.allowList.some((ip) => ip.address === '0.0.0.0' && ip.prefixLen === 0),
      );
      setAllowList(
        value.allowList.map((ip) => {
          return {
            IpRange: `${ip.address}/${ip.prefixLen}`,
            Description: ip.description,
          };
        }),
      );
    }
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleIpEnter = (ipRange: string, description: string) => {
    const validIp = isValidIpRange(ipRange);

    if (validIp && !allowList.some((entry) => entry.IpRange === ipRange)) {
      const newList: AllowListEntry[] = allowList.filter((entry) => entry.IpRange !== '0.0.0.0/0');
      const entry: AllowListEntry = {
        IpRange: ipRange,
        Description: description || '',
      };
      newList.push(entry);
      setAllowList(newList);
      onChange(Array.from(newList));
    }
  };

  const handleRemoveAllowListEntry = (addressToRemove: string) => {
    const newList = allowList.filter((entry) => entry.IpRange !== addressToRemove);
    setAllowList(newList);
    onChange(Array.from(newList));
  };

  const readOnlyClasses = isReadOnly ? 'pointer-events-none opacity-50' : '';

  return (
    <Widget
      header={
        <Typography className="text-neutral-text-weak" variant="body-medium">
          Select Allowed IP Addresses
        </Typography>
      }
    >
      <div className={`mx-2 flex flex-col gap-4 ${readOnlyClasses}`}>
        <div className="flex flex-col gap-2">
          <Radio
            label="All"
            isChecked={allSelected}
            onChange={() => {
              onChange([{ IpRange: '0.0.0.0/0', Description: '' }]);
              setAllSelected(true);
            }}
          />
          <Radio
            label="Specific range of IP addresses"
            isChecked={!allSelected}
            onChange={() => {
              setAllSelected(false);
              const filtered = Array.from(allowList).filter((entry) => entry.IpRange !== '0.0.0.0/0');
              onChange(filtered);
            }}
          />
        </div>
        {!allSelected && (
          <div>
            <div className="flex w-full items-center gap-2">
              <TextInput
                className="flex-grow"
                isFluid
                label="Range of IP addresses"
                helpText="Enter ranges in CIDR format (e.g. 192.168.0.0/24)."
                value={ipInput}
                onChange={(event) => setIpInput(event.target.value)}
              />
              <TextInput
                className="flex-grow"
                isFluid
                label="Description"
                isOptional
                value={descriptionInput}
                onChange={(event) => setDescriptionInput(event.target.value)}
                helpText="Provide a description for this IP address/range."
              />
            </div>
            <Tooltip type="simple" isDisabled={!exceedsMaxSize} placement="top" isPortaled={false}>
              <Tooltip.Trigger hasButtonWrapper>
                <Button
                  className="mt-3"
                  color="neutral"
                  fill="outlined"
                  isDisabled={exceedsMaxSize || !isValidIpRange(ipInput) || !ipInput.trim()}
                  onClick={() => {
                    const trimmedIp = ipInput.trim();
                    if (trimmedIp) {
                      handleIpEnter(trimmedIp, descriptionInput);
                      setIpInput('');
                      setDescriptionInput('');
                    }
                  }}
                >
                  Add IP address/range
                </Button>
              </Tooltip.Trigger>
              <Tooltip.Content>{`You can not add more than ${maxAllowListSize} ranges.`}</Tooltip.Content>
            </Tooltip>
            <div className="mt-4">
              <AllowListTable allowList={Array.from(allowList)} onRemoveEntry={handleRemoveAllowListEntry} />
            </div>
          </div>
        )}
      </div>
    </Widget>
  );
};
